import { DEFAULT_SPEED, normalizeSpeed } from "../app/playbackSpeed";
import {
  cloneAudioParams,
  defaultAudioParams,
  validateAudioParams,
  type AudioParams,
} from "../audio/params";
import { DEFAULT_GUIDE_MODE_ID, getGuideMode } from "../help/guideModes";
import type { Preset } from "../presets";
import {
  cloneRenderParams,
  cloneSimParams,
  defaultRenderParams,
  defaultSimParams,
  validateSimParams,
  type RenderParams,
  type SimParams,
} from "../sim/params";

export interface AppStateV1 {
  v: 1;
  preset: string;
  dirty: boolean;
  playing: boolean;
  /** Target sim steps per frame (0.25…8). */
  stepsPerFrame: number;
  /** UI guide mode id: guided | compact | lab */
  guide: string;
  params: SimParams;
  render: RenderParams;
  /** Track A sonification — omit on legacy URLs → defaults (off). */
  audio: AudioParams;
}

export function stateFromPreset(preset: Preset, playing = true): AppStateV1 {
  return {
    v: 1,
    preset: preset.id,
    dirty: false,
    playing,
    stepsPerFrame: DEFAULT_SPEED,
    guide: DEFAULT_GUIDE_MODE_ID,
    params: cloneSimParams(preset.params),
    render: cloneRenderParams(preset.render),
    audio: cloneAudioParams(preset.audio ?? defaultAudioParams()),
  };
}

export function encodeState(state: AppStateV1): string {
  const json = JSON.stringify(state);
  const b64 = btoa(unescape(encodeURIComponent(json)));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function decodeState(hash: string): AppStateV1 | null {
  try {
    const raw = hash.startsWith("#") ? hash.slice(1) : hash;
    if (!raw.startsWith("s=")) return null;
    let b64 = raw.slice(2).replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const json = decodeURIComponent(escape(atob(b64)));
    const data = JSON.parse(json) as AppStateV1;
    if (data.v !== 1 || !data.params || !data.render) return null;
    data.params = validateSimParams({ ...defaultSimParams(), ...data.params });
    data.render = { ...defaultRenderParams(), ...data.render };
    // Ephemeral: never restore selection from URL (Track Z1 / C7)
    data.render.selectedIndex = -1;
    // Legacy URLs: velocityColor false without colorMode → uniform
    if (data.render.colorMode == null) {
      data.render.colorMode =
        data.render.velocityColor === false ? "uniform" : "velocity";
    }
    if (data.render.backend == null) data.render.backend = "auto";
    // Track A: legacy URLs omit audio → off defaults (no unlock on decode)
    data.audio = validateAudioParams({
      ...defaultAudioParams(),
      ...(data.audio ?? {}),
    });
    data.guide = getGuideMode(data.guide).id;
    data.stepsPerFrame = normalizeSpeed(data.stepsPerFrame ?? DEFAULT_SPEED);
    return data;
  } catch {
    return null;
  }
}

export function writeUrl(state: AppStateV1): void {
  const enc = encodeState(state);
  const url = `${location.pathname}${location.search}#s=${enc}`;
  history.replaceState(null, "", url);
}

/**
 * Persist state into the URL hash and return the full shareable href.
 * Clipboard interaction is owned by ShareModal — do not silent-copy here.
 */
export function prepareShareUrl(state: AppStateV1): string {
  writeUrl(state);
  return location.href;
}

/** @deprecated use prepareShareUrl + ShareModal */
export async function copyShareUrl(state: AppStateV1): Promise<string> {
  return prepareShareUrl(state);
}
